import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";
import { FiSend, FiLayers, FiGrid, FiUserCheck } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import { MIN_REGISTER_DATA } from "./storeReportsData";

const MaterialIssueRegisterReport = () => {
  const gridRef = useRef(null);
  const [data] = useState(MIN_REGISTER_DATA);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalIssues = data.length;
    const uniqueWorkOrders = new Set(data.map((r) => r.workOrderRef)).size;
    const uniqueWorkAreas = new Set(data.map((r) => r.workArea)).size;
    const uniqueEngineers = new Set(data.map((r) => r.authorizedBy)).size;

    return {
      totalIssues,
      uniqueWorkOrders,
      uniqueWorkAreas,
      uniqueEngineers,
    };
  }, [data]);

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "MIN Number",
        field: "minNo",
        colId: "minNo",
        flex: 1.2,
        minWidth: 140,
        cellRenderer: (p) => (
          <span
            style={{
              fontWeight: 700,
              color: "var(--color-primary, #0b6462)",
              fontFamily: "monospace",
              fontSize: "12px",
            }}
          >
            {p.value}
          </span>
        ),
      },
      {
        headerName: "Date & Time Issued",
        field: "dateTime",
        colId: "dateTime",
        flex: 1.3,
        minWidth: 145,
        valueFormatter: (p) => (p.value ? moment(p.value).format("DD-MMM-YYYY HH:mm") : "—"),
      },
      {
        headerName: "Work Order / Subcontractor Ref",
        field: "workOrderRef",
        colId: "workOrderRef",
        flex: 1.8,
        minWidth: 200,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#1e293b" }}>{p.value}</span>,
      },
      {
        headerName: "Work Area / Location",
        field: "workArea",
        colId: "workArea",
        flex: 2,
        minWidth: 220,
        cellRenderer: (p) => <span style={{ color: "#334155" }}>{p.value}</span>,
      },
      {
        headerName: "Material SKU & Description",
        field: "materialDetail",
        colId: "materialDetail",
        flex: 2,
        minWidth: 220,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "var(--color-primary, #0b6462)" }}>{p.value}</span>
        ),
      },
      {
        headerName: "Quantity Issued",
        field: "qtyIssued",
        colId: "qtyIssued",
        flex: 1.3,
        minWidth: 140,
        cellRenderer: (p) => <span style={{ fontWeight: 700, color: "#0f172a" }}>{p.value}</span>,
      },
      {
        headerName: "Issued To (Contractor Rep / Foreman)",
        field: "issuedTo",
        colId: "issuedTo",
        flex: 1.6,
        minWidth: 180,
      },
      {
        headerName: "Authorized By (Site Engineer)",
        field: "authorizedBy",
        colId: "authorizedBy",
        flex: 1.5,
        minWidth: 170,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#475569" }}>{p.value}</span>,
      },
    ];
  }, []);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    [],
  );

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const q = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (item.minNo && item.minNo.toLowerCase().includes(q)) ||
        (item.workOrderRef && item.workOrderRef.toLowerCase().includes(q)) ||
        (item.workArea && item.workArea.toLowerCase().includes(q)) ||
        (item.materialDetail && item.materialDetail.toLowerCase().includes(q)) ||
        (item.issuedTo && item.issuedTo.toLowerCase().includes(q)) ||
        (item.authorizedBy && item.authorizedBy.toLowerCase().includes(q));

      return matchesSearch;
    });
  }, [data, searchTerm]);

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Material_Issue_Register_MIN_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("MIN issue register exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL MIN DISPATCHES"
            value={`${kpis.totalIssues} Store Notes`}
            subtitle="Store dispatches to field"
            icon={FiSend}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="WORK PACKAGES ACTIVE"
            value={`${kpis.uniqueWorkOrders} Contracts`}
            subtitle="Receiving active dispatches"
            icon={FiLayers}
            color="#0284c7"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="WORK AREAS SERVED"
            value={`${kpis.uniqueWorkAreas} Locations`}
            subtitle="Towers, basements & slabs"
            icon={FiGrid}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="AUTHORIZING ENGINEERS"
            value={`${kpis.uniqueEngineers} Engineers`}
            subtitle="Verified sign-offs on record"
            icon={FiUserCheck}
            color="#8b5cf6"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (General Journal style) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="Material Issue Register"
            >
              Material Issue Register
            </div>
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search MIN, work order, area, SKU..."
            />

            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialIssueRegisterReport;
